import { Dialog } from "$components/dialog/dialog";
import { useConfig } from "$global-providers/config-provider/provider";
import { TERRITORY } from "$routes/_lib/constants";
import {
  Box,
  Checkbox,
  makeStyles,
  styled,
  Typography,
} from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import React from "react";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";

const defaultConsentMessage = defineMessage({
  id: "patient.consent.body.default",
  defaultMessage:
    "We are using a system called ‘Elephant’ to create digital health records. To keep your information safe it will be encrypted and stored on servers in the European Union and processed in accordance with Elephant’s Privacy Policy, which is available at the web link printed on your card.",
});
const kenyaConsentMessage = defineMessage({
  id: "patient.consent.body.kenya",
  defaultMessage:
    "We are using a system called ‘Elephant’ to create digital health records. To keep your information safe it will be encrypted and stored on servers in Kenya and/or the European Union and processed in accordance with Elephant’s Privacy Policy, which is available at the web link printed on your card.",
});
const pakistanConsentMessage = defineMessage({
  id: "patient.consent.body.pakistan",
  defaultMessage:
    "We are using a system called ‘Elephant’ to create digital health records. To keep your information safe it will be encrypted, stored securely, and processed in accordance with Elephant’s Privacy Policy, which is available at the web link printed on your ICT health card.",
});

function useTranslatedConsentInfoMsg() {
  const { territory } = useConfig();
  const { formatMessage } = useIntl();
  switch (territory) {
    case TERRITORY.KENYA:
      return formatMessage(kenyaConsentMessage);
    case TERRITORY.PAKISTAN:
      return formatMessage(pakistanConsentMessage);
    default:
      return formatMessage(defaultConsentMessage);
  }
}

const useStyles = makeStyles((theme) => ({
  warning: {
    borderRadius: "5px",
  },
  warningBreakout: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: "8px",
  },
  warningIcon: {
    color: theme.palette.warning.main,
    height: 50,
    width: 50,
  },
  checkbox: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  checkboxLabel: {
    cursor: "pointer",
    userSelect: "none",
  },
}));

const Body = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const CHECKBOX_ID = "patient-consent-checkbox";

export const TermsOfRegistrationDialog = ({
  open = false,
  onClose,
  onSubmit,
}) => {
  const [confirmed, setConfirmed] = React.useState(false);
  const classes = useStyles();
  const translatedConsentMessage = useTranslatedConsentInfoMsg();
  const { formatMessage } = useIntl();

  return (
    <Dialog
      open={open}
      title={formatMessage({
        id: "patient.consent.title.default",
        defaultMessage: "Terms of Registration",
      })}
      onSubmit={onSubmit}
      submitLabel={formatMessage({
        id: "patient.consent.confirmationBtn",
        defaultMessage: "Accept & Continue",
      })}
      submitDisabled={!confirmed}
      onCancel={onClose}
      cancelLabel={formatMessage({
        id: "patient.consent.denyBtn",
        defaultMessage: "Cancel",
      })}
    >
      <Body>
        <Box p={2} display="flex" className={classes.warningBreakout}>
          <Box mr={2}>
            <ErrorOutlineIcon className={classes.warningIcon} />
          </Box>
          <Typography className={classes.warningBreakout}>
            <FormattedMessage
              id="patient.consent.warning"
              defaultMessage="Please show or read this text to the patient, or to their parent or guardian if the patient is under 18 years of age."
            />
          </Typography>
        </Box>
        <Box p={0.5}>
          <Typography variant="body2">{translatedConsentMessage}</Typography>
        </Box>

        <Box display="flex" alignItems="center">
          <Box mr={2}>
            <Checkbox
              autoFocus
              id={CHECKBOX_ID}
              value={confirmed}
              onChange={(e) => {
                setConfirmed(e.target.checked);
              }}
              className={classes.checkbox}
              inputProps={{
                "data-testid": "patientConsentCheckbox",
              }}
            />
          </Box>
          <label htmlFor={CHECKBOX_ID} className={classes.checkboxLabel}>
            <Typography variant="body2">
              <FormattedMessage
                id="patient.consent.confirmation"
                defaultMessage="Do you confirm that you are happy for you or your child’s information to be processed in this way?"
              />
            </Typography>
          </label>
        </Box>
      </Body>
    </Dialog>
  );
};
